import {
  styled,
  Box as MuiBox,
  List as MuiList,
  Drawer as MuiDrawer,
  MenuItem as MuiMenuItem,
  IconButton as MuiIconButton,
  ListItemButton as MuiListItemButton,
} from '@mui/material'
import type { Theme, CSSObject } from '@mui/material/styles'

const DRAWER_WIDTH = 270

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
})

const closedMixin = (theme: Theme): CSSObject => ({
  width: '76px',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  flexShrink: 0,
  width: DRAWER_WIDTH,
  overflowX: 'hidden',

  '& .MuiDrawer-paperAnchorDockedLeft': {
    padding: '24px 0 24px 16px',
    borderRight: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: 'none',
    overflowX: 'hidden',
  },

  ...(open === true && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
    '& .SidebarLogo': {
      opacity: 1,
      visibility: 'visible',
      transitionDelay: `${theme.transitions.duration.enteringScreen}ms`,
    },
    '& .SidebarListItemButton': {
      maxWidth: 'calc(100% - 16px)',
      '& .MuiTypography-root': {
        opacity: 1,
      },
    },
    '& .SidebarSettings': {
      flexDirection: 'row',
    },
    '& .SidebarFooter': {
      p: {
        opacity: 1,
      },
    },
  }),

  ...(open === false && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
    '& .SidebarLogo': {
      opacity: 0,
      visibility: 'hidden',
    },
    '& .SidebarListItemButton': {
      maxWidth: '44px',
      '& .MuiTypography-root': {
        opacity: 0,
      },
    },
    '& .SidebarSettings': {
      flexDirection: 'column',
    },
    '& .SidebarUser': {
      display: 'none',
    },
    '& .SidebarFooter': {
      p: {
        width: 0,
        opacity: 0,
      },
    },
  }),
}))

const Heading = styled('h2')({
  margin: 0,
})

const Logo = styled(
  'img',
  {}
)(({ theme }) => ({
  width: '100%',
  maxWidth: '100px',
  height: 'auto',
  position: 'absolute',
  top: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  transitionProperty: 'opacity, visibility',
  transitionDuration: `${theme.transitions.duration.enteringScreen}ms`,
  transitionTimingFunction: theme.transitions.easing.sharp,
}))

const List = styled(MuiList)(() => ({
  marginTop: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}))

const ListItemButton = styled(MuiListItemButton)(({ theme }) => ({
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: '44px',
  borderRadius: '6px',
  color: theme.palette.grey[700],

  '&:hover, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus-visible': {
    color: theme.palette.grey[800],
    backgroundColor: theme.palette.grey[200],
  },
}))

const ListItemIcon = styled('span')(() => ({
  display: 'block',
  lineHeight: 0,

  svg: {
    width: '20px',
    height: '20px',
  },
}))

const MenuItemUser = styled(MuiMenuItem)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  svg: {
    width: '20px',
    height: '20px',
  },
}) as typeof MuiMenuItem

const WrapperSettings = styled(MuiBox)({
  margin: 'auto 0 16px',
  display: 'flex',
  gap: '10px',
  width: 'calc(100% - 16px)',
})

const LogoLanguage = styled('img')({
  width: '20px',
  height: '20px',
})

const WrapperUser = styled(MuiBox)(({ theme }) => ({
  marginBottom: '16px',
  padding: '16px 44px 16px 12px',
  position: 'relative',
  width: 'calc(100% - 16px)',
  minHeight: '72px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.grey[300]}`,
}))

const ButtonLogout = styled(MuiIconButton)({
  position: 'absolute',
  top: '50%',
  right: '4px',
  transform: 'translateY(-50%)',
})

const LogoPlej = styled('img')({
  width: '100%',
  maxWidth: '44px',
  height: 'auto',
})

const Footer = styled(MuiBox)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: 'calc(100% - 16px)',
})

export {
  Drawer,
  Heading,
  Logo,
  List,
  ListItemButton,
  ListItemIcon,
  MenuItemUser,
  WrapperSettings,
  LogoLanguage,
  WrapperUser,
  ButtonLogout,
  LogoPlej,
  Footer,
}
